import { useState, useEffect } from "react";
import { FilterIcon } from "./CustomSVG";
import CreatorCard from "./CreatorCard";
import { FaStar, FaTrophy, FaMedal } from "react-icons/fa6";
import { API_BASE } from "../utils/Instance";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function ContentContributorsUI({ onContributorClick, initialSearch = "" }) {
  const [showFilters, setShowFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [contributors, setContributors] = useState([]);
  const [filteredContributors, setFilteredContributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const { profile, setProfile } = useAuth();

  useEffect(() => {
    const checkIfMobile = () => setIsMobile(window.innerWidth < 1024);
    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  // Initial fetch and when URL search param (initialSearch) changes
  useEffect(() => {
    const term = (initialSearch !== undefined && initialSearch !== "") ? initialSearch : searchQuery;
    if (initialSearch !== undefined && initialSearch !== "") setSearchQuery(initialSearch);
    fetchContributors(term);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearch]);

  const fetchContributors = async (searchTerm) => {
    const query = searchTerm !== undefined ? searchTerm : searchQuery;
    try {
      setLoading(true);
      const url = new URL(`${API_BASE}accounts/contributors/`);
      if (query && query.trim()) url.searchParams.set("search", query.trim());
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${profile?.accessToken}`
        }
      });

      if (!response.ok) {
        throw new Error("Failed to fetch contributors");
      }

      const data = await response.json();
      if (data.viewer_wallet_balance != null) {
        const walletBalance = Number(data.viewer_wallet_balance);
        if (Number.isFinite(walletBalance)) {
          setProfile((prev) => ({
            ...prev,
            walletBalance,
            wallet_balance: walletBalance,
          }));
        }
      }

      // Extract contributors array from response
      const contributorsArray = data.contributors || data.results || (Array.isArray(data) ? data : []);
      
      if (!Array.isArray(contributorsArray)) {
        console.error("Invalid data format:", data);
        throw new Error("Invalid data format received from API");
      }

      // Transform API data to match component structure - ALL DATA FROM DATABASE
      const transformedData = contributorsArray.map(contributor => ({
        // Profile data from DB
        id: contributor.id,
        name: contributor.screen_name || contributor.name,
        img: contributor.avatar || contributor.profile_picture || contributor.profilePicture, // From database
        age: contributor.age,
        gender: contributor.gender,
        
        // Body measurements from DB
        bust: contributor.bust_size || contributor.bustSize,
        penisSize: contributor.penis_size || contributor.penisSize,
        bodyType: contributor.body_type || contributor.bodyType,
        hairColor: contributor.hair_color || contributor.hairColor,
        skinTone: contributor.skin_tone || contributor.skinTone,
        height: contributor.height,
        weight: contributor.weight,
        shoeSize: contributor.shoe_size || contributor.shoeSize,
        
        // Contest statistics from DB
        contests: contributor.contests || contributor.contests_count || contributor.contestsCount || 0,
        badges: contributor.badges || contributor.badges_count || contributor.badgesCount || 0,
        top10: contributor.top10 || 0,
        
        // Photo galleries from DB - includes real images!
        photoGalleries: contributor.photo_galleries || contributor.photoGalleries || "0 of 7",
        photoGallery: contributor.photoGallery || {}, // Contains actual body part images from DB
      }));

      setContributors(transformedData);
      setFilteredContributors(transformedData);
      if (query && query.trim()) {
        toast.success(`Found ${transformedData.length} contributors matching "${query.trim()}"`);
      } else {
        toast.success(`Loaded ${transformedData.length} contributors`);
      }
    } catch (error) {
      console.error("Error fetching contributors:", error);
      toast.error("Failed to load contributors. Please try again.");
      // Keep empty array if fetch fails
      setContributors([]);
      setFilteredContributors([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle checkbox change
  const handleFilterChange = (category, value, isChecked) => {
    setSelectedFilters(prev => {
      const categoryFilters = prev[category] || [];
      if (isChecked) {
        return { ...prev, [category]: [...categoryFilters, value] };
      } else {
        return { ...prev, [category]: categoryFilters.filter(v => v !== value) };
      }
    });
  };

  // Apply filters function
  const applyFilters = () => {
    console.log('🔍 Applying filters...', selectedFilters);
    let filtered = [...contributors];
    console.log('📊 Total contributors before filtering:', filtered.length);

    // Apply each filter category
    Object.entries(selectedFilters).forEach(([category, values]) => {
      if (values.length === 0) return;
      console.log(`🎯 Applying ${category} filter with values:`, values);

      filtered = filtered.filter(contributor => {
        switch (category) {
          case "Gender":
            return values.includes(contributor.gender);
          
          case "Age":
            return values.some(range => {
              const age = contributor.age;
              if (!age) return false;
              if (range === "18–21") return age >= 18 && age <= 21;
              if (range === "22–25") return age >= 22 && age <= 25;
              if (range === "26–29") return age >= 26 && age <= 29;
              if (range === "30–34") return age >= 30 && age <= 34;
              if (range === "35–40") return age >= 35 && age <= 40;
              if (range === "40+") return age > 40;
              return false;
            });
          
          case "Skin Tone":
            return values.some(tone => 
              contributor.skinTone?.toLowerCase() === tone.toLowerCase()
            );
          
          case "Hair Color":
            return values.some(color => 
              contributor.hairColor?.toLowerCase() === color.toLowerCase()
            );
          
          case "Body Type":
            return values.some(type => 
              contributor.bodyType?.toLowerCase() === type.toLowerCase()
            );
          
          case "Shoe Size":
            return values.some(range => {
              const shoeSize = parseFloat(contributor.shoeSize);
              if (!shoeSize) return false;
              if (range === "<6") return shoeSize < 6;
              if (range === "6–7.5") return shoeSize >= 6 && shoeSize <= 7.5;
              if (range === "8–9.5") return shoeSize >= 8 && shoeSize <= 9.5;
              if (range === "10+") return shoeSize >= 10;
              if (range === "<8.5") return shoeSize < 8.5;
              if (range === "8.5–10") return shoeSize >= 8.5 && shoeSize <= 10;
              if (range === "10.5–12.5") return shoeSize >= 10.5 && shoeSize <= 12.5;
              if (range === "13+") return shoeSize >= 13;
              return false;
            });
          
          case "Height":
            return values.some(range => {
              const heightStr = contributor.height;
              if (!heightStr) return false;
              // Parse height like "5'10"" or "5'10" to inches
              const match = heightStr.match(/(\d+)'(\d+)/);
              if (!match) return false;
              const heightInches = parseInt(match[1]) * 12 + parseInt(match[2]);
              
              if (range === '<5\'1"') return heightInches < 61;
              if (range === '5\'1"–5\'4"') return heightInches >= 61 && heightInches <= 64;
              if (range === '5\'5"–5\'8"') return heightInches >= 65 && heightInches <= 68;
              if (range === '5\'9"–6\'') return heightInches >= 69 && heightInches <= 72;
              if (range === '>6\'') return heightInches > 72;
              if (range === ' <5\'6"') return heightInches < 66;
              if (range === '5\'6"–5\'9"') return heightInches >= 66 && heightInches <= 69;
              if (range === '5\'10"–6\'1"') return heightInches >= 70 && heightInches <= 73;
              if (range === '6\'2"–6\'5"') return heightInches >= 74 && heightInches <= 77;
              if (range === '>6\'5"') return heightInches > 77;
              return false;
            });
          
          case "Weight":
            return values.some(range => {
              const weightStr = contributor.weight;
              if (!weightStr) return false;
              const weight = parseFloat(weightStr.replace(/[^\d.]/g, ''));
              
              if (range === "<110 lbs") return weight < 110;
              if (range === "110–129 lbs") return weight >= 110 && weight <= 129;
              if (range === "130–149 lbs") return weight >= 130 && weight <= 149;
              if (range === "150–174 lbs") return weight >= 150 && weight <= 174;
              if (range === "175–200 lbs") return weight >= 175 && weight <= 200;
              if (range === ">200 lbs") return weight > 200;
              if (range === "<150 lbs") return weight < 150;
              if (range === "200–224 lbs") return weight >= 200 && weight <= 224;
              if (range === "225–249 lbs") return weight >= 225 && weight <= 249;
              if (range === "250–299 lbs") return weight >= 250 && weight <= 299;
              if (range === ">299 lbs") return weight > 299;
              return false;
            });
          
          case "Bust Size":
            return values.some(size => {
              const bust = contributor.bust;
              if (!bust) return false;
              if (size === ">DDD") {
                return bust.includes("G") || bust.includes("H") || bust.includes("I");
              }
              return bust.includes(size);
            });
          
          case "Penis Size":
            return values.some(range => {
              const penisStr = contributor.penisSize;
              if (!penisStr) return false;
              const size = parseFloat(penisStr.replace(/[^\d.]/g, ''));
              
              if (range === '<4"') return size < 4;
              if (range === '4–5.5"') return size >= 4 && size <= 5.5;
              if (range === '6–7.5"') return size >= 6 && size <= 7.5;
              if (range === '8–9.5"') return size >= 8 && size <= 9.5;
              if (range === '10+"') return size >= 10;
              return false;
            });
          
          case "Photo Gallery":
            return values.some(galleryType => {
              const photoGallery = contributor.photoGallery || {};
              return photoGallery.hasOwnProperty(galleryType);
            });
          
          default:
            return true;
        }
      });
    });

    console.log('✅ Filtered contributors:', filtered.length);
    setFilteredContributors(filtered);
    toast.success(`Found ${filtered.length} contributors matching filters`);
    
    // Close filters on mobile after applying
    if (isMobile) {
      setShowFilters(false);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedFilters({});
    setFilteredContributors(contributors);
    toast.info("Filters cleared");
  };

  const toggleFilters = () => setShowFilters((prev) => !prev);

  const filterCategories = [
  { name: "Gender", options: ["Male", "Female", "Other"] },
  {
    name: "Age",
    options: ["18–21", "22–25", "26–29", "30–34", "35–40", "40+"]
  },
  { name: "Skin Tone", options: ["Light", "Medium", "Dark"] },
  {
    name: "Hair Color",
    options: [
      "Blonde",
      "Brown",
      "Black",
      "Red",
      "Auburn",
      "Dirty Blonde",
      "Strawberry Blonde"
    ]
  },
  {
  name: "Body Type",
  options: [
    { label: "Women", heading: true, className: "text-white" },
    "Petite",
    "Short & Curvy",
    "Avg/Athletic",
    "Tall & Slender",
    "Plus Size",
    
    
    { label: "Men", heading: true, className: "text-white" },
    "Short & Fit",
    "Short & Broad",
    "Avg/Athletic",
    "Tall & Fit",
    "Big & Tall"
  ]
},
  {
    name: "Shoe Size",
    options: [
       { label: "Women", heading: true, className: "!text-white font-semibold" },

      "<6",
      "6–7.5",
      "8–9.5",
      "10+",
      { label: "Men", heading: true, style: { color: "white" } },
      "<8.5",
      "8.5–10",
      "10.5–12.5",
      "13+"
    ]
  },
  {
    name: "Height",
    options: [
       { label: "Women", heading: true, className: "!text-white font-semibold" },

      '<5\'1"',
      '5\'1"–5\'4"',
      '5\'5"–5\'8"',
      '5\'9"–6\'',
      '>6\'',
      { label: "Men", heading: true, style: { color: "white" } },
      ' <5\'6"',
      '5\'6"–5\'9"',
      '5\'10"–6\'1"',
      '6\'2"–6\'5"',
      '>6\'5"'
    ]
  },
  {
    name: "Weight",
    options: [
      { label: "Women", heading: true, className: "!text-white font-semibold" },

      "<110 lbs",
      "110–129 lbs",
      "130–149 lbs",
      "150–174 lbs",
      "175–200 lbs",
      ">200 lbs",
      { label: "Men", heading: true, style: { color: "white" } },
      "<150 lbs",
      "150–174 lbs",
      "175–199 lbs",
      "200–224 lbs",
      "225–249 lbs",
      "250–299 lbs",
      ">299 lbs"
    ]
  },
  {
    name: "Bust Size",
    options: ["AA", "A", "B", "C", "D", "DD/E", "DDD/F", ">DDD"]
  },
  {
    name: "Penis Size",
   options: ['<4"', '4–5.5"', '6–7.5"', '8–9.5"', '10+"']
  },
  {
    name: "Photo Gallery",
    options: ["Teaser", "Full Body", "Chest & Stomach", "Butt", "P/V", "Legs", "Feet"]
  }
];



  return (
    <div className="flex flex-col min-h-screen w-full bg-[#1C1C1E] text-white">
   <div className="p-4 border-b border-gray-700">
  <div className="flex justify-between items-center">
    {/* Left: Filters Button */}
    <button
      onClick={toggleFilters}
      className="text-2xl p-2 rounded-md focus:outline-none text-white flex items-center gap-2"
    >
      <FilterIcon />
      <span className="text-base font-medium">Filters</span>
    </button>
    <div className="flex-1 text-center">
      <h1 className="text-xl md:text-2xl font-bold text-[#ceb46a] mb-8">
        Content Contributors
      </h1>
    </div>

    <div className="w-[84px] md:w-[128px]" /> 
  </div>

  <div className="flex flex-wrap justify-start md:justify-center gap-4 text-base text-white px-2 mt-4">
  <div className="flex items-center gap-2">
    <FaTrophy className="text-[#ceb46a] text-2xl" />
    <span className="font-medium">1st Place</span>
  </div>
  <div className="flex items-center gap-2">
    <FaMedal className="text-[#ceb46a] text-2xl" />
    <span className="font-medium">Top 3</span>
  </div>
  <div className="flex items-center gap-2">
    <FaStar className="text-[#ceb46a] text-2xl" />
    <span className="font-medium">Top 10</span>
  </div>
</div>

  <div className="flex gap-2 mt-4">
    <input
      type="text"
      placeholder="Search by screen name or name (if enabled)"
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          fetchContributors(searchQuery);
        }
      }}
      className="flex-1 py-2 px-3 bg-[#2A2A2D] border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-[#ceb46a]"
    />
    <button
      type="button"
      onClick={() => fetchContributors(searchQuery)}
      className="py-2 px-4 bg-[#ceb46a] text-black font-medium rounded-lg hover:bg-[#b9a05a] transition"
    >
      Search
    </button>
  </div>

</div>
      {showFilters && isMobile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={toggleFilters}></div>
      )}

      <div className="flex flex-1 w-full overflow-hidden">
        {showFilters && (
          <div className={`w-full md:w-[300px] bg-[#0f0f10] border-r border-gray-700 p-4 overflow-y-auto z-50 fixed md:static inset-0 md:inset-auto h-full md:h-auto transition-transform duration-300 ${isMobile ? "block" : "hidden"} md:block`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Filters</h2>
              {isMobile && <button onClick={toggleFilters} className="text-white text-xl">✕</button>}
            </div>
          <div className="space-y-4">
  {filterCategories.map((cat) => (
    <div key={cat.name}>
      <label className="block text-sm mb-1 font-semibold text-[#ceb46a]">{cat.name}</label>
      <div className="flex flex-wrap gap-2">
        {cat.options.map((option, idx) => {
          if (typeof option === "object" && option.heading) {
            return (
              <div
                key={`heading-${idx}`}
                className="w-full text-xs font-bold text-white mt-2 mb-1 uppercase tracking-wide"
              >
                {option.label}
              </div>
            );
          } else {
            return (
              <label key={`option-${idx}`} className="text-sm flex items-center gap-1 cursor-pointer">
                <input 
                  type="checkbox" 
                  value={option} 
                  checked={(selectedFilters[cat.name] || []).includes(option)}
                  onChange={(e) => handleFilterChange(cat.name, option, e.target.checked)}
                  className="accent-[#ceb46a] cursor-pointer" 
                />
                {option}
              </label>
            );
          }
        })}
      </div>
    </div>
  ))}
  <div className="flex gap-2">
    <button 
      onClick={applyFilters}
      className="flex-1 mt-2 py-2 bg-[#ceb46a] text-black font-semibold rounded-full hover:bg-[#b9a05a] transition"
    >
      Apply Filters
    </button>
    <button 
      onClick={clearFilters}
      className="flex-1 mt-2 py-2 bg-gray-700 text-white font-semibold rounded-full hover:bg-gray-600 transition"
    >
      Clear
    </button>
  </div>
</div>
          </div>
        )}

        <div className="flex-1 px-4 py-6 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#ceb46a]"></div>
              <p className="text-gray-400">Loading contributors...</p>
            </div>
          ) : contributors.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <p className="text-gray-400 text-lg">No contributors found</p>
              <button
                onClick={fetchContributors}
                className="px-4 py-2 bg-[#ceb46a] text-black font-medium rounded-lg hover:bg-[#b9a05a] transition"
              >
                Retry
              </button>
            </div>
          ) : filteredContributors.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <p className="text-gray-400 text-lg">No contributors match your filters</p>
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-[#ceb46a] text-black font-medium rounded-lg hover:bg-[#b9a05a] transition"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              <div className="mb-4 text-gray-400 text-sm">
                Showing {filteredContributors.length} of {contributors.length} contributors
              </div>
              <div className={`grid gap-4 md:gap-6 ${showFilters && !isMobile ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'} auto-rows-fr`}>
                {filteredContributors.map((c, i) => (
                  <CreatorCard
                    key={c.id || i}
                    {...c}
                    onClick={() => {
                      if (onContributorClick) onContributorClick(c);
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
